import { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Grid, Divider } from '@mui/material';
import { getInvoiceById } from '@/shared/service/request/alumio';
import B3Spin from '@/components/spin/B3Spin';
import { snackbar } from '@/utils';
import { useB3Lang } from '@b3/lang';


interface InvoiceDetailCardProps {
  row: {
    id: string;
    createdAt: number;
    updatedAt: number;
  };
}

export function InvoiceDetailCard({ row }: InvoiceDetailCardProps) {
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const b3Lang = useB3Lang();

  useEffect(() => {
    const fetchInvoiceData = async () => {
      setLoading(true);
      try {
        const data = await getInvoiceById(row.id);
        const firstItem = Array.isArray(data.results) ? data.results[0] : data.results;

        setInvoiceData(firstItem);
      } catch (error) {
        snackbar.error('Failed to load invoice data.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceData();
  }, [row.id]);

  return (
    <B3Spin isSpinning={loading}>
      <Box sx={{ marginTop: 4, padding: 4, backgroundColor: '#fafafa', borderRadius: 2 }}>
        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={6}>
            <Box sx={{ marginBottom: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {b3Lang('invoiceDetail.extraInformation')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {b3Lang('invoiceDetail.invoiceId')}: <strong>{row.id}</strong>
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {b3Lang('invoiceDetail.createdAt')}:{' '}
                <strong>
                  {invoiceData?.CreationDateTime
                    ? new Date(
                      parseInt(invoiceData.CreationDateTime.match(/\d+/)?.[0] || '0'),
                    ).toLocaleDateString()
                    : 'N/A'}
                </strong>
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Amounts {b3Lang('invoiceDetail.amounts')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {b3Lang('invoiceDetail.totalGross')}:{' '}
                <strong>
                  {Number(invoiceData?.TotalGrossAmount).toFixed(2)}{' '}
                  {invoiceData?.TotalGrossAmountCurrencyCode}
                </strong>
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {b3Lang('invoiceDetail.totalNet')}:{' '}
                <strong>
                  {Number(invoiceData?.TotalNetAmount).toFixed(2)}{' '}
                  {invoiceData?.TotalNetAmountCurrencyCode}
                </strong>
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {b3Lang('invoiceDetail.totalTax')}:{' '}
                <strong>
                  {Number(invoiceData?.TotalTaxAmount).toFixed(2)}{' '}
                  {invoiceData?.TotalTaxAmountCurrencyCode}
                </strong>
              </Typography>
            </Box>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={6}>
            <Box sx={{ marginBottom: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {b3Lang('invoiceDetail.invoiceSummary')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {b3Lang('invoiceDetail.date')}:{' '}
                <strong>
                  {invoiceData?.Date
                    ? new Date(
                      parseInt(invoiceData.Date.match(/\d+/)?.[0] || '0'),
                    ).toLocaleDateString()
                    : 'N/A'}
                </strong>
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {b3Lang('invoiceDetail.status')}: <strong>{invoiceData?.ReleaseStatusCodeText}</strong>
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {b3Lang('invoiceDetail.paymentTerms')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {invoiceData?.CashDiscountTerms?.PaymentTermsCodeText || 'N/A'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {b3Lang('invoiceDetail.paymentBaseline')}:{' '}
                <strong>
                  {invoiceData?.CashDiscountTerms?.PaymentBaselineDate
                    ? new Date(
                      parseInt(
                        invoiceData.CashDiscountTerms.PaymentBaselineDate.match(/\d+/)?.[0] ||
                        '0',
                      ),
                    ).toLocaleDateString()
                    : 'N/A'}
                </strong>
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {b3Lang('invoiceDetail.fullPaymentEnd')}:{' '}
                <strong>
                  {invoiceData?.CashDiscountTerms?.FullPaymentEndDate
                    ? new Date(
                      parseInt(
                        invoiceData.CashDiscountTerms.FullPaymentEndDate.match(/\d+/)?.[0] || '0',
                      ),
                    ).toLocaleDateString()
                    : 'N/A'}
                </strong>
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider sx={{ marginY: 4 }} />

        {/* Items Section */}
        {invoiceData?.Item && invoiceData.Item.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              {b3Lang('invoiceDetail.items')}
            </Typography>
            <Grid container spacing={3}>
              {invoiceData.Item.map((item: any, index: number) => (
                <Grid item xs={12} key={index}>
                  <Card sx={{ borderRadius: 3, boxShadow: 3, padding: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        {item.Description || b3Lang('itemDescription.fallback')}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {b3Lang('invoiceDetail.quantity')}: <strong>{Number(item.Quantity).toFixed(2)}</strong>{' '}
                            {item.QuantityUnitCodeText}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {b3Lang('invoiceDetail.grossAmount')}: <strong>{Number(item.GrossAmount).toFixed(2)}</strong>{' '}
                            {item.GrossAmountCurrencyCode}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {b3Lang('invoiceDetail.netAmount')}: <strong>{Number(item.NetAmount).toFixed(2)}</strong>{' '}
                            {item.NetAmountCurrencyCode}
                          </Typography>
                          <Typography variant="body2">
                            {b3Lang('invoiceDetail.taxAmount')}: <strong>{Number(item.TaxAmount).toFixed(2)}</strong>{' '}
                            {item.TaxAmountCurrencyCode}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </B3Spin>
  );
}
